import { useState, useRef, useEffect } from "react";
import { BASE_URL } from "../apiConfig";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  chartType?: "bar" | "line" | "heatmap";
  chartData?: ChartData<"bar" | "line">;
  chartOptions?: ChartOptions<"bar" | "line">;
  heatmap?: {
    title?: string;
    xLabels: string[];
    values: number[];
    valueLabel?: string;
  };
};

const COLOR_PALETTE = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ab",
];

function normalizeChartJsOptions(
  opts: any,
  titleText?: string
): ChartOptions<"bar" | "line"> {
  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: Boolean(titleText || opts?.title?.display),
        text: titleText || opts?.title?.text || "",
      },
      legend: {
        display: opts?.plugins?.legend?.display ?? true,
        position: opts?.plugins?.legend?.position ?? "top",
      },
      tooltip: { enabled: true },
    },
    scales: {
      x: { title: { display: Boolean(opts?.xAxisTitle), text: opts?.xAxisTitle } },
      y: {
        beginAtZero:
          opts?.scales?.y?.beginAtZero ??
          opts?.scales?.yAxes?.[0]?.ticks?.beginAtZero ??
          true,
        title: { display: Boolean(opts?.yAxisTitle), text: opts?.yAxisTitle },
        ticks: {
          ...opts?.scales?.y?.ticks,
          ...(opts?.scales?.yAxes?.[0]?.ticks || {}),
        },
      },
    },
  };
  return options;
}

// Build datasets with colors
function buildDatasetsFromSeries(
  series: Record<string, number[]>,
  labelMap?: Record<string, string>
) {
  const keys = Object.keys(series);
  return keys.map((k, i) => ({
    label: (labelMap && labelMap[k]) || k,
    data: series[k],
    borderColor: COLOR_PALETTE[i % COLOR_PALETTE.length],
    backgroundColor: COLOR_PALETTE[i % COLOR_PALETTE.length] + "88",
    fill: false,
  }));
}

function pickFirstNumericKey(obj: Record<string, any>) {
  const keys = Object.keys(obj);
  for (const k of keys) {
    if (typeof obj[k] === "number") return k;
  }
  return null;
}

function normalizeVisualization(viz: any): {
  type?: "bar" | "line" | "heatmap";
  data?: ChartData<"bar" | "line">;
  options?: ChartOptions<"bar" | "line">;
  heatmap?: ChatMessage["heatmap"];
} {
  // allow inference even if type is missing
  if (!viz) return {};

  // 1) Bar charts
  if (viz.type === "bar") {
    // Case: dates + series: [{ name, values }]
    if (viz?.data?.dates && Array.isArray(viz?.data?.series)) {
      const labels = viz.data.dates;
      const datasets = viz.data.series.map((s: any, i: number) => ({
        label: s?.name || `Series ${i + 1}`,
        data: Array.isArray(s?.values) ? s.values : [],
        backgroundColor: COLOR_PALETTE[i % COLOR_PALETTE.length] + "88",
        borderColor: COLOR_PALETTE[i % COLOR_PALETTE.length],
      }));
      const options = normalizeChartJsOptions(
        { xAxisTitle: viz?.x_axis, yAxisTitle: viz?.y_axis },
        viz?.title
      );
      return { type: "bar", data: { labels, datasets }, options };
    }
    // Case A: Already Chart.js-like
    if (viz?.data?.labels && viz?.data?.datasets) {
      const titleText = viz?.options?.title?.text || viz?.title;
      const options = normalizeChartJsOptions(
        viz?.options,
        titleText
      );
      return {
        type: "bar",
        data: {
          labels: viz.data.labels,
          datasets: viz.data.datasets.map((d: any, i: number) => ({
            ...d,
            backgroundColor:
              d.backgroundColor || COLOR_PALETTE[i % COLOR_PALETTE.length] + "88",
            borderColor: d.borderColor || COLOR_PALETTE[i % COLOR_PALETTE.length],
          })),
        },
        options,
      };
    }

    // Case B: Array of objects [{store_id, footfall}, ...]
    if (Array.isArray(viz?.data) && viz.data.length > 0) {
      const sample = viz.data[0];
      // Try to detect labelKey (categorical) and valueKey (numeric)
      let labelKey =
        Object.keys(sample).find((k) =>
          ["store_id", "sku_id", "date", "label", "name"].includes(k)
        ) || Object.keys(sample).find((k) => typeof sample[k] === "string") || "label";

      let valueKey =
        Object.keys(sample).find((k) =>
          ["footfall", "value", "sales", "quantity", "qty", "count", "amount"].includes(k)
        ) || pickFirstNumericKey(sample) || "value";

      const labels = viz.data.map((d: any) => String(d[labelKey]));
      const data = viz.data.map((d: any) => Number(d[valueKey]));
      const options = normalizeChartJsOptions(
        {
          xAxisTitle: viz?.x_axis,
          yAxisTitle: viz?.y_axis,
        },
        viz?.title
      );
      return {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: viz?.y_axis || "Value",
              data,
              backgroundColor: COLOR_PALETTE[0] + "88",
              borderColor: COLOR_PALETTE[0],
            },
          ],
        },
        options,
      };
    }

    // Case C: Chart.js-like under visualization.data but with labels/datasets in a nested shape
    if (viz?.data?.labels && viz?.data?.datasets) {
      const options = normalizeChartJsOptions(viz?.options, viz?.title);
      return { type: "bar", data: viz.data, options };
    }
  }

  // 2) Line charts
  if (viz.type === "line") {
    // Case: dates + series: [{ name, values }]
    if (viz?.data?.dates && Array.isArray(viz?.data?.series)) {
      const labels = viz.data.dates;
      const datasets = viz.data.series.map((s: any, i: number) => ({
        label: s?.name || `Series ${i + 1}`,
        data: Array.isArray(s?.values) ? s.values : [],
        borderColor: COLOR_PALETTE[i % COLOR_PALETTE.length],
        backgroundColor: COLOR_PALETTE[i % COLOR_PALETTE.length] + "33",
        fill: false,
      }));
      const options = normalizeChartJsOptions(
        { xAxisTitle: viz?.x_axis, yAxisTitle: viz?.y_axis },
        viz?.title
      );
      return { type: "line", data: { labels, datasets }, options };
    }
    // Case A: Already Chart.js-like
    if (viz?.data?.labels && viz?.data?.datasets) {
      const options = normalizeChartJsOptions(viz?.options, viz?.title);
      return { type: "line", data: viz.data, options };
    }

    // Case B: arrays with dates and multiple series
    // viz.data = { dates: [...], series1: [...], ... }, viz.labels has human labels
    if (viz?.data?.dates) {
      const labels = viz.data.dates;
      const labelMap = viz?.labels || {};
      const series: Record<string, number[]> = {};
      for (const k of Object.keys(viz.data)) {
        if (k === "dates") continue;
        if (Array.isArray(viz.data[k])) {
          series[k] = viz.data[k];
        }
      }
      const datasets = buildDatasetsFromSeries(series, labelMap);
      const options = normalizeChartJsOptions(
        {
          xAxisTitle: viz?.labels?.x || "X",
          yAxisTitle: viz?.labels?.y || "Y",
        },
        viz?.title
      );
      return {
        type: "line",
        data: { labels, datasets },
        options,
      };
    }
  }

  // 3) Heatmap (custom renderer)
  if (viz.type === "heatmap") {
    // Expecting viz.data.days and viz.data.footfall (1D heat intensity)
    const days: string[] =
      viz?.data?.days ||
      viz?.data?.labels ||
      [];
    const values: number[] =
      viz?.data?.footfall ||
      viz?.data?.values ||
      [];
    return {
      type: "heatmap",
      heatmap: {
        title: viz?.title,
        xLabels: days,
        values,
        valueLabel: viz?.y_axis || "Value",
      },
    };
  }

  // Fallback: infer bar chart from dates + series when type is missing
  if (viz?.data?.dates && Array.isArray(viz?.data?.series)) {
    const labels = viz.data.dates;
    const datasets = viz.data.series.map((s: any, i: number) => ({
      label: s?.name || `Series ${i + 1}`,
      data: Array.isArray(s?.values) ? s.values : [],
      backgroundColor: COLOR_PALETTE[i % COLOR_PALETTE.length] + "88",
      borderColor: COLOR_PALETTE[i % COLOR_PALETTE.length],
    }));
    const options = normalizeChartJsOptions(
      { xAxisTitle: viz?.x_axis, yAxisTitle: viz?.y_axis },
      viz?.title
    );
    return { type: "bar", data: { labels, datasets }, options };
  }

  return {};
}

function Heatmap1D({
  title,
  xLabels,
  values,
  valueLabel,
}: NonNullable<ChatMessage["heatmap"]>) {
  const min = Math.min(...values);
  const max = Math.max(...values);

  const colorFor = (v: number) => {
    if (max === min) return "hsl(200, 60%, 70%)";
    const t = (v - min) / (max - min); // 0..1
    // Map 0..1 to green->red
    const hue = 120 - 120 * t; // 120=green, 0=red
    return `hsl(${hue}, 70%, ${50 + (1 - t) * 10}%)`;
  };

  return (
    <div className="w-full max-w-3xl mt-2">
      {title && <div className="text-sm font-semibold mb-2">{title}</div>}
      <div className="flex gap-1 items-end">
        {xLabels.map((label, i) => (
          <div key={label} className="flex flex-col items-center">
            <div
              title={`${label}: ${values[i]}`}
              className="w-10 h-10 rounded"
              style={{ background: colorFor(values[i]) }}
            />
            <div className="text-xs mt-1 text-slate-600">{label}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-500 mt-2">
        {valueLabel || "Value"} scale: min {min} — max {max}
      </div>
    </div>
  );
}

/* Chat message bubble */
function MessageBubble({
  role,
  children,
  onCopy,
}: {
  role: "user" | "assistant";
  children: React.ReactNode;
  onCopy?: () => void;
}) {
  const isUser = role === "user";
  return (
    <div className={`flex w-full gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="mt-1 h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" stroke="#111827" fill="none">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c1.5-4 6.5-4 8-4s6.5 0 8 4" />
          </svg>
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${
          isUser ? "bg-[#111827] text-white" : "bg-white border border-slate-200 text-slate-900"
        }`}
      >
        <div className="prose prose-sm max-w-none">
          {children}
        </div>
        {!isUser && (
          <div className="mt-2 flex gap-2">
            <button
              onClick={onCopy}
              className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
              title="Copy"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="9" y="9" width="10" height="10" rx="2" />
                <rect x="5" y="5" width="10" height="10" rx="2" />
              </svg>
            </button>
          </div>
        )}
      </div>
      {isUser && (
        <div className="mt-1 h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center shrink-0">
          <span className="text-xs font-semibold">You</span>
        </div>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 text-slate-500">
      <span className="inline-flex gap-1">
        <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.2s]" />
        <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.1s]" />
        <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" />
      </span>
      <span className="text-xs">thinking…</span>
    </div>
  );
}

const SUGGESTIONS = [
  "What if discount +15% on SKU0002 in Store S002?",
  "Top 10 SKUs by margin this month",
  "If footfall +20% in Store S010, what happens to sales?"
];

export default function SimulatorTab() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null); // NEW
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Scroll only the chat area, not the page
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const autoResize = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "0px";
    const h = Math.min(160, textareaRef.current.scrollHeight);
    textareaRef.current.style.height = h + "px";
  };

  const getHistoryString = () => {
    // Get last 5 exchanges (user/assistant pairs)
    const history: string[] = [];
    for (let i = Math.max(0, messages.length - 10); i < messages.length; i++) {
      const msg = messages[i];
      if (msg.role === "user") history.push(`User: ${msg.content}`);
      if (msg.role === "assistant") history.push(`Assistant: ${msg.content}`);
    }
    return history.join("\n");
  };

  const sendMessage = async (prompt?: string) => {
    const text = (prompt ?? input).trim();
    if (!text || loading) return;

    setLoading(true);
    setError(null);
    setInput("");

    setMessages((msgs) => [...msgs, { role: "user", content: text }]);

    try {
      const res = await fetch(`${BASE_URL}/simulate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          message: text,
          history: getHistoryString(), // <-- send history
        }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const payload = await res.json();

      const insight: string = payload?.data?.insight || payload?.message || "No response.";

      let assistantMsg: ChatMessage = { role: "assistant", content: insight };

      const viz = payload?.data?.visualization;
      const norm = normalizeVisualization(viz);

      if ((norm.type === "bar" || norm.type === "line") && norm.data) {
        assistantMsg.chartType = norm.type;
        assistantMsg.chartData = norm.data as ChartData<"bar" | "line">;
        assistantMsg.chartOptions = norm.options as ChartOptions<"bar" | "line">;
      } else if (norm.type === "heatmap" && norm.heatmap) {
        assistantMsg.chartType = "heatmap";
        assistantMsg.heatmap = norm.heatmap;
      }

      setMessages((msgs) => [...msgs, assistantMsg]);
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const onCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  useEffect(() => autoResize(), [input]);

  // NEW: control scrollbar visibility based on explicit newlines
  const hasNewline = input.includes("\n");

  return (
    <div className="p-6 md:p-8">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="font-semibold text-slate-900">Simulator</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Ask a question and view instant charts, comparisons, and heatmaps.
            </p>
          </div>
        </div>

        {/* Suggestions (only when empty) */}
        {messages.length === 0 && (
          <div className="px-5 pt-5">
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat area */}
        <div
          ref={chatAreaRef} // NEW
          className="px-5 pt-4 pb-2"
          style={{
            maxHeight: "70vh",
            height: messages.length === 0 ? "120px" : "420px",
            minHeight: messages.length === 0 ? "80px" : "200px",
            overflowY: "auto",
          }}
        >
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              Start by selecting a suggestion or typing a query below.
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((m, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <MessageBubble role={m.role} onCopy={() => onCopy(m.content)}>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </MessageBubble>

                  {/* Visualization block */}
                  {m.chartType === "bar" && m.chartData && (
                    <div className="ml-11 mt-1 w-full max-w-3xl">
                      <div className="h-[300px] rounded-xl border border-slate-200 bg-white p-3">
                        <Bar
                          data={m.chartData as ChartData<"bar">}
                          options={m.chartOptions as ChartOptions<"bar">}
                        />
                      </div>
                    </div>
                  )}
                  {m.chartType === "line" && m.chartData && (
                    <div className="ml-11 mt-1 w-full max-w-3xl">
                      <div className="h-[300px] rounded-xl border border-slate-200 bg-white p-3">
                        <Line
                          data={m.chartData as ChartData<"line">}
                          options={m.chartOptions as ChartOptions<"line">}
                        />
                      </div>
                    </div>
                  )}
                  {m.chartType === "heatmap" && m.heatmap && (
                    <div className="ml-11">
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <Heatmap1D
                          title={m.heatmap.title}
                          xLabels={m.heatmap.xLabels}
                          values={m.heatmap.values}
                          valueLabel={m.heatmap.valueLabel}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="ml-11">
                  <TypingIndicator />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pb-2">
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center justify-between">
              <span>Error: {error}</span>
              <button
                onClick={() => sendMessage()}
                className="text-red-700 hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
          <div className="flex items-center gap-2 flex-nowrap">  {/* was items-end */}
            <div className="flex-1 relative min-w-0">             {/* added min-w-0 */}
              <textarea
                ref={textareaRef}
                className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Ask anything…"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={autoResize}
                disabled={loading}
                style={{ overflowY: hasNewline ? "auto" : "hidden" }} // no scrollbar until newline
              />
            </div>
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#111827] px-4 py-2 text-white text-sm font-medium disabled:opacity-50"  
              title="Send"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}